import { useState, useEffect, ReactNode, createContext, Dispatch, SetStateAction } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useStoreState } from "ezStore";
import { useQuery } from "@apollo/client";

import { Layout as CCKLayout, emptyFunction } from "@cofano/cck";
import { faShip } from "@fortawesome/pro-solid-svg-icons";

import LayoutTop from "./LayoutTop";
import Navigation from "./Navigation";
import LayoutDrawer from "./LayoutDrawer";
import LayoutEnvironmentBanner from "./LayoutEnvironmentBanner";

import { GetVersionDocument } from "__generated__/graphql/queries.generated";

export type LayoutDrawerSettingsState = {
    open: boolean;
    title: string;
    content: ReactNode;
	size: "normal" | "large",
	variant: "inlay" | "overlay" | "overlayWithMask",
};

export type LayoutContextType = {
	drawerSettings: LayoutDrawerSettingsState;
    setDrawerSettings: Dispatch<SetStateAction<LayoutDrawerSettingsState>>;
	closeDrawer: () => void,
}

const defaultDrawerSettings: LayoutDrawerSettingsState = {
	open: false,
	title: "",
	content: null,
	size: "normal",
	variant: "inlay",
};

// eslint-disable-next-line react-refresh/only-export-components
export const LayoutContext = createContext<LayoutContextType>({
	drawerSettings: defaultDrawerSettings,
	setDrawerSettings: emptyFunction,
	closeDrawer: emptyFunction,
});

const Layout = ({ children }: { children: ReactNode }) => {

	const { settings, user } = useStoreState(store => store.session);

	const location = useLocation();
	const navigate = useNavigate();

	const [ drawerSettings, setDrawerSettings ] = useState<LayoutDrawerSettingsState>(defaultDrawerSettings);

	const { data } = useQuery(GetVersionDocument);

	const onCloseDrawer = () => {
		setDrawerSettings(defaultDrawerSettings);
	};

	useEffect(() => {
		if (drawerSettings.open) {
			onCloseDrawer();
		}
	}, [ location ]);

	const convertLogo = () => {
		const { prefix, iconName, icon } = faShip;
		return `data:image/svg+xml;base64,${btoa(
			`<svg data-prefix="${prefix}" data-icon="${iconName}"
			xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${icon[0]} ${icon[1]}">
			<path fill="white" d="${icon[4]}"></path>
		  </svg>`)}`;
	};

	const isCofanoUser = user?.email?.includes("@cofano");

	return (
		<LayoutContext.Provider
			value={{
				drawerSettings,
				setDrawerSettings,
				closeDrawer: onCloseDrawer,
			}}>
			<div>
				<CCKLayout
					navigate={(url, event) => {
						if (event.ctrlKey) {
							window.open(url, "_blank");
						} else {
							navigate(url);
						}
					}}>
					<CCKLayout.Top
						logo={convertLogo()}
						title={"BOS"}
						onLogoClick={() => navigate("/")}
						customer={settings?.settings?.customer}>
						<LayoutTop />
					</CCKLayout.Top>

					<CCKLayout.Wrapper>
						<CCKLayout.Sidebar version={data?.version} onVersionClick={() => navigate("/view/changelog")}>
							<Navigation path={location?.pathname} />
						</CCKLayout.Sidebar>

						<CCKLayout.Main>
							{children}
							{isCofanoUser && <LayoutEnvironmentBanner />}
						</CCKLayout.Main>

						{drawerSettings.open && (
							<LayoutDrawer
								drawerSettings={drawerSettings}
								onClose={onCloseDrawer} />
						)}
					</CCKLayout.Wrapper>

					<div id="date-picker-container" />
				</CCKLayout>
			</div>
		</LayoutContext.Provider>
	);
};

export default Layout;
